/**
 * How Malka names the other party on a call, everywhere it is named.
 *
 * Both ends of a call have to agree. The outgoing side derives the label on
 * the device from the persisted match (`vrs_active_call`); the incoming side
 * cannot, because iOS reports a VoIP push to CallKit from a background process
 * before any JavaScript is running, so the push has to arrive carrying a
 * finished string. Two derivations would drift, so both call this one.
 *
 * The rule:
 *   - queue-matched call  -> the peer's phone leg, e.g. `VRS (562) 353-2502`
 *   - queue-matched, none -> '' (the caller supplies the app name)
 *   - point-to-point      -> the peer's name
 *   - nothing known       -> ''
 *
 * A queue-matched call shows a number rather than the interpreter's name on
 * purpose: the interpreter is assigned rather than chosen, and this label ends
 * up on lock screens and in the phone's own call history. Point-to-point calls
 * show the name, because the user picked that person.
 *
 * Plain CommonJS so the Node server can require it directly and both app
 * bundles can import it. Keep it that way.
 *
 * The one dependency is libphonenumber-js/min, because national formatting
 * rules are per-country and change, and hand-rolling them gets other countries
 * wrong. It is already a dependency of vrs-server and of the app, and the RN
 * bundles already carry it for the dial pad. Resolution from this directory is
 * the delicate part: the server image deletes /app/node_modules, so
 * Dockerfile.vrs-server places the package under shared/node_modules. If that
 * ever stops happening this module throws on load, which is the failure we
 * want — the alternative is the two ends of a call formatting numbers
 * differently and nobody noticing.
 */

const phoneLib = require('libphonenumber-js/min');
const parsePhoneNumberFromString = phoneLib.parsePhoneNumberFromString
    || (phoneLib.default && phoneLib.default.parsePhoneNumberFromString);

const SERVICE_LABELS = {
    vri: 'VRI',
    vrs: 'VRS'
};

/**
 * The service prefix for a queue-matched call — the `VRS` in
 * `VRS (562) 353-2502`. Names the kind of call, which the app name shown
 * beside it does not.
 *
 * @param {string} callType The `callType` from the match payload.
 * @returns {string}
 */
function serviceLabel(callType) {
    const key = String(callType == null ? '' : callType).toLowerCase();

    return SERVICE_LABELS[key] || SERVICE_LABELS.vrs;
}

/**
 * A phone number formatted for a call label.
 *
 * North American numbers read as a local number — `(562) 353-2502` — which is
 * how the people on these calls say them, and the country code would be noise
 * on every row. Everything else keeps its country code and its own national
 * grouping, `+44 20 7946 0958`, which is the only way it is unambiguous.
 *
 * @param {string} value A phone number in any stored shape.
 * @returns {string} The formatted number, or '' when there isn't one.
 */
function formatPeerPhone(value) {
    const raw = String(value == null ? '' : value).trim();

    if (!raw) {
        return '';
    }

    const digits = raw.replace(/\D/g, '');

    if (!digits) {
        return '';
    }

    // A leading + is authoritative; without one the digits are a NANP number,
    // which is what every provisioned VRS number and dialable target is.
    const parsed = parsePhoneNumberFromString(raw.charAt(0) === '+' ? raw : digits, 'US');

    if (parsed && parsed.isPossible()) {
        return String(parsed.countryCallingCode) === '1'
            ? parsed.formatNational()
            : parsed.formatInternational();
    }

    // Numbers the library cannot place still have to render something: short
    // codes and test lines reach here.
    const national = digits.length === 11 && digits.charAt(0) === '1'
        ? digits.slice(1)
        : digits;

    if (national.length === 10) {
        return '(' + national.slice(0, 3) + ') ' + national.slice(3, 6) + '-' + national.slice(6);
    }

    return raw.charAt(0) === '+' ? '+' + digits : digits;
}

/**
 * A phone leg rendered as a call label — `VRS (562) 353-2502`.
 *
 * @param {string} callType The `callType` from the match payload.
 * @param {string} phone The peer's phone number.
 * @returns {string} The label, or '' when there is no usable number.
 */
function getPhoneCallLabel(callType, phone) {
    const formatted = formatPeerPhone(phone);

    return formatted ? serviceLabel(callType) + ' ' + formatted : '';
}

/**
 * The number of the party at the other end of the phone leg.
 *
 * An interpreter's peer is the Deaf client, identified by the VRS number the
 * call was placed from — or, on an inbound hearing call, dialed to. A client's
 * peer is the hearing party they called, or who called them.
 *
 * @param {Object} call The match payload or persisted active call.
 * @param {string} role The role of whoever is being shown the label.
 * @returns {string} The peer's number, possibly ''.
 */
function getPeerPhoneNumber(call, role) {
    if (!call) {
        return '';
    }

    const phone = role === 'interpreter' ? call.callerIdPhone : call.targetPhone;

    return phone == null ? '' : String(phone);
}

/**
 * The label for the other party on a call.
 *
 * @param {Object} call The match payload or persisted active call.
 * @param {string} role The role of whoever is being shown the label.
 * @returns {string} The label, or '' when only an app name applies. Callers
 * supply that themselves, because it is the receiving device that knows which
 * Malka app it is.
 */
function getCallPeerLabel(call, role) {
    if (!call) {
        return '';
    }

    // An assigned interpreter is what makes a call queue-matched; the
    // point-to-point path never sets one.
    if (call.interpreterId) {
        return getPhoneCallLabel(call.callType, getPeerPhoneNumber(call, role));
    }

    return String(call.clientName == null ? '' : call.clientName).trim();
}

module.exports = {
    formatPeerPhone,
    getCallPeerLabel,
    getPeerPhoneNumber,
    getPhoneCallLabel
};
